import React from 'react';
import {
  Chip,
  Grid,
  Typography,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Box,
  Table,
  TableBody,
  TableRow,
  TableCell,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import LinkIcon from '@material-ui/icons/Link';
import {
  useEntity,
  EntityRefLink,
  useRelatedEntities,
} from '@backstage/plugin-catalog-react';
import { ProtoDefinitionWithBrowser } from './ProtoDefinitionWithBrowser';
import { Entity, RELATION_HAS_PART } from '@backstage/catalog-model';

const useStyles = makeStyles(theme => ({
  badge: {
    margin: theme.spacing(0.5),
  },
  section: {
    marginBottom: theme.spacing(2),
  },
  metadata: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  transportChip: {
    fontWeight: 'bold',
  },
}));

function TransportBadge({ transport }: { transport: string }) {
  const classes = useStyles();
  const transports = transport.split(',').map(t => t.trim());
  return (
    <>
      {transports.map(t => (
        <Chip
          key={t}
          label={t.toUpperCase()}
          color={t === 'pubsub' ? 'primary' : 'secondary'}
          size="small"
          className={classes.transportChip}
        />
      ))}
    </>
  );
}

/** Widget for `type: event` — the envelope/topic entity. */
export function EventDefinitionWidget({ definition }: { definition: string }) {
  const classes = useStyles();
  const { entity } = useEntity();
  const annotations = entity.metadata.annotations ?? {};
  const {
    entities: subEvents,
    loading,
  } = useRelatedEntities(entity, { type: RELATION_HAS_PART });

  const transport = annotations['playgoals.com/event-default-transport'] ?? 'unknown';
  const topic = annotations['playgoals.com/event-topic'];
  const stream = annotations['playgoals.com/event-stream'];
  const hasOutbox = annotations['playgoals.com/event-outbox'] === 'true';
  const hasBqIngestion = annotations['playgoals.com/event-bq-ingestion'] === 'true';

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Box className={classes.metadata}>
          <TransportBadge transport={transport} />
          {hasOutbox && (
            <Chip
              label="Outbox"
              size="small"
              variant="outlined"
            />
          )}
          {hasBqIngestion && (
            <Chip
              label="BQ Ingestion"
              size="small"
              variant="outlined"
            />
          )}
        </Box>
      </Grid>

      {(topic || stream) && (
        <Grid item xs={12}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle2" gutterBottom>
                Transport Details
              </Typography>
              <Table size="small">
                <TableBody>
                  {topic && (
                    <TableRow>
                      <TableCell variant="head">Pub/Sub Topic</TableCell>
                      <TableCell><code>{topic}</code></TableCell>
                    </TableRow>
                  )}
                  {stream && (
                    <TableRow>
                      <TableCell variant="head">NATS Stream</TableCell>
                      <TableCell><code>{stream}</code></TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </Grid>
      )}

      {!loading && subEvents && subEvents.length > 0 && (
        <Grid item xs={12}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle2" gutterBottom>
                Sub-Events ({subEvents.length})
              </Typography>
              <List dense>
                {subEvents.map((subEvent: Entity) => {
                  const filterValue =
                    subEvent.metadata.annotations?.[
                      'playgoals.com/event-filter-value'
                    ] ?? '';
                  return (
                    <React.Fragment key={subEvent.metadata.name}>
                      <ListItem>
                        <ListItemIcon>
                          <LinkIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <EntityRefLink entityRef={subEvent}>
                              {subEvent.metadata.title ??
                                subEvent.metadata.name}
                            </EntityRefLink>
                          }
                          secondary={
                            filterValue
                              ? `event_type: ${filterValue}`
                              : subEvent.metadata.description
                          }
                        />
                      </ListItem>
                      <Divider variant="inset" component="li" />
                    </React.Fragment>
                  );
                })}
              </List>
            </CardContent>
          </Card>
        </Grid>
      )}

      <Grid item xs={12}>
        <ProtoDefinitionWithBrowser definition={definition} label="Envelope Schema" />
      </Grid>
    </Grid>
  );
}

/** Widget for `type: sub-event` — individual event within an envelope. */
export function SubEventDefinitionWidget({
  definition,
}: {
  definition: string;
}) {
  const { entity } = useEntity();
  const annotations = entity.metadata.annotations ?? {};
  const filterValue =
    annotations['playgoals.com/event-filter-value'] ?? '';
  const parentRef = annotations['playgoals.com/event-parent'];

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Box display="flex" flexWrap="wrap" gridGap={8}>
          {filterValue && (
            <Chip
              label={`event_type: ${filterValue}`}
              size="small"
              variant="outlined"
            />
          )}
          {parentRef && (
            <Chip
              icon={<LinkIcon />}
              label={
                <EntityRefLink entityRef={`api:default/${parentRef}`}>
                  {parentRef}
                </EntityRefLink>
              }
              size="small"
              variant="outlined"
            />
          )}
        </Box>
      </Grid>

      <Grid item xs={12}>
        <ProtoDefinitionWithBrowser definition={definition} label="Message Schema" />
      </Grid>
    </Grid>
  );
}
